import AppUtils from "../AppUtils";
import { getDivTemplate, getSpanTemplate, getLiTemplate } from "../../PdfTemplates/prescriptionBody";
import { isEmpty as _isEmpty } from "lodash";
import { TreatmentPlanFollowUpObj, TreatmentPlanReferralObj, TreatmentPlanMedicinesObj, TreatmentPlanLabTestObj, TreatmentPlanAdviceObj } from "../../models/ITreatmentPlanTemplateResponse";
import { IPdfTemplates } from "../../models/PdfTemplatesModels";
import { PrescriptionPdfSection, AppConstants } from "../AppConstants";
import { ITreatmentPlanPdfData } from "../../components/templates/CommonPrescriptionPdf";
export interface TreatmentPlanMedicinesIndexObj extends TreatmentPlanMedicinesObj { arrIndex: number }

export interface ITreatmentPlanPdf {
    followup: Array<TreatmentPlanFollowUpObj>
    referral: Array<TreatmentPlanReferralObj>
    prescriptionDiagnosis: Array<string>
    prescriptionLabTest: Array<TreatmentPlanLabTestObj>
    prescriptionMedicines: Array<TreatmentPlanMedicinesIndexObj>
    prescriptionAdvice: Array<TreatmentPlanAdviceObj>
}

export const treatmentPlanFormatState = (props: ITreatmentPlanPdfData): ITreatmentPlanPdf => {
    let newPrescription = {} as ITreatmentPlanPdf;
    if (!_isEmpty(props) && !_isEmpty(props.templateData)) {
        if (AppUtils.isDefined(props.templateData.diagnosis)) {
            newPrescription.prescriptionDiagnosis = props.templateData.diagnosis
        }
        if (!_isEmpty(props.templateData.template)) {
            let prescriptionItem = props.templateData.template;
            newPrescription.followup = prescriptionItem.prescriptionInstructions && prescriptionItem.prescriptionInstructions.followupInfoSet
                ? prescriptionItem.prescriptionInstructions.followupInfoSet
                : null;
            newPrescription.referral = prescriptionItem.prescriptionInstructions && prescriptionItem.prescriptionInstructions.referralInfoSet
                ? prescriptionItem.prescriptionInstructions.referralInfoSet
                : null;
            if (prescriptionItem.prescriptionLabTest && prescriptionItem.prescriptionLabTest.labTestList) {
                newPrescription.prescriptionLabTest = prescriptionItem.prescriptionLabTest.labTestList
            }
            if (prescriptionItem.prescriptionMedicines && prescriptionItem.prescriptionMedicines.prescriptionMedicationList) {
                newPrescription.prescriptionMedicines = prescriptionItem.prescriptionMedicines.prescriptionMedicationList.map(
                    (meds, index) => {
                        return { ...meds, arrIndex: index}
                    })
            }
            if (prescriptionItem.prescriptionAdvice && prescriptionItem.prescriptionAdvice.advice) {
                newPrescription.prescriptionAdvice = prescriptionItem.prescriptionAdvice.advice
            }
        }
    }
    return newPrescription;
}

export const treatmentPlanModifyTemplate = (
    pdfTemplates: IPdfTemplates,
    props: ITreatmentPlanPdfData,
    prescription: Readonly<ITreatmentPlanPdf>,
    pageIndex?: number,
    templateList?: any[]
): string => {

    const getDoctorDetailsTemplate = () => {
        const { doctorProfile } = props;
        if (pdfTemplates.templateNameToDataMap && pdfTemplates.templateNameToDataMap["doctorDetails"] && doctorProfile) {
            let doctorPrefix = !_isEmpty(doctorProfile.prefix) ? doctorProfile.prefix + " " : ""
            return pdfTemplates.templateNameToDataMap["doctorDetails"]
                .replace("$doctorName", doctorPrefix + !_isEmpty(doctorProfile.name) ? doctorProfile.name : "")
                .replace("$doctorQualifications", !_isEmpty(doctorProfile.qualification) ? doctorProfile.qualification : "")
                .replace("$doctorKmc", !_isEmpty(doctorProfile.registrationNumber) ? doctorProfile.registrationNumber : "")
                .replace("$centerName", "");
        }
        return "";
    }

    const showDosageRow = (medicine: TreatmentPlanMedicinesIndexObj) => {
        return (!_isEmpty(medicine.frequency) || !_isEmpty(medicine.medicineDuration))
    }

    const checkInstructionsExist = (medicine: TreatmentPlanMedicinesIndexObj) => {
        if ((!_isEmpty(medicine.medicineDosage)) || !_isEmpty(medicine.medicineInstructions) || (medicine.comments)) {
            return true;
        } else {
            return false;
        }
    }

    const getPrescriptionCardHeaderTemplate = () => {
        if (pdfTemplates.templateNameToDataMap["prescriptionCardHeader"]) {
            const date = AppUtils.getDayEpochInfo(new Date().valueOf());
            return pdfTemplates.templateNameToDataMap["prescriptionCardHeader"]
                .replace("$patientName", props.templateData.templateName)
                .replace("$date", `${date.dateText} ${date.month}, ${date.year}`)
                .replace("$uhid", "NA")
                .replace("$reasonForVisit", "")
        }
        return "";
    }

    const getPrescriptionPdfTemplate = () => {
        const totalPages = (templateList || []).length
        let prescriptionTemp = "";
        const headerTemplate = pdfTemplates.templateNameToDataMap["carefitHeader"];
        let doctorDetailsTemplate = getDoctorDetailsTemplate();
        let prescriptionCardHeaderTemplate = getPrescriptionCardHeaderTemplate();

        /* HEADER + DOCTOR DETAILS */
        prescriptionTemp += headerTemplate + doctorDetailsTemplate;

        let prescriptionBodyTemp = getDivTemplate("patient-prescription-body");
        let prescriptionBodyCardContTemp = getDivTemplate(`prescription-card`);
        let prescriptionCardContentTemplate = getDivTemplate("prescription-card-content-container").replace("$injectDivData", "$injectPrescriptionBody");

        /* PRESCRIPTION BODY(PATIENT'S DETAILS + SECTIONS BODY + SIGNATURE CONTAINER) + FOOTER */
        prescriptionTemp += prescriptionBodyTemp.replace(
            "$injectDivData",
            prescriptionBodyCardContTemp.replace(
                "$injectDivData", prescriptionCardHeaderTemplate
                + "<hr/>"
            + prescriptionCardContentTemplate
            )
            + getDivTemplate("prescription-page-index").replace(
                "$injectDivData", `Page :&nbsp;&#10222;&nbsp;${pageIndex && pageIndex > 0
                    ? pageIndex + 1
                    : 1
                } / ${totalPages || 1}&nbsp;&#10223;`
            )
        );

        return getDivTemplate("patient-prescription-template")
            .replace("$injectDivData", prescriptionTemp);
    };

    const getDiagnosisTemplate = () => {
        if (prescription && prescription.prescriptionDiagnosis && prescription.prescriptionDiagnosis.length > 0) {
            const diagnosisTemplate = pdfTemplates.templateNameToDataMap["diagnosis"];
            const diagnosisBody = prescription.prescriptionDiagnosis.map((diagnosis: string) => {
                const diagnosisItemTemplate = pdfTemplates.templateNameToDataMap["diagnosisItem"];
                return diagnosisItemTemplate
                    .replace("$injectDiagnosis", diagnosis)
                    .replace("$injectDiagnosisType", "")
                    .replace("$injectDiagnosisComment", "")
            })
            return diagnosisTemplate.replace("$injectDiagnosisBody", diagnosisBody.join("\n"));
        }
        return "";
    }

    const getLabTestsTemplate = () => {
        if (prescription && prescription.prescriptionLabTest && prescription.prescriptionLabTest.length > 0) {
            const labTestContTemplate = pdfTemplates.templateNameToDataMap["labTest"];
            const labTestBodyItems = prescription.prescriptionLabTest.map((test: TreatmentPlanLabTestObj) => {
                const labTestBodyTemplate = getDivTemplate();
                let labTestBody = "";
                labTestBody += getSpanTemplate("prescription-subheading").replace(
                    "$injectSpanData",
                    (test.diagnosis && !_isEmpty(test.diagnosis.displayValue)) ? test.diagnosis.displayValue : ""
                );
                if (test.labTestFrequency && (test.labTestFrequency.count || test.labTestFrequency.unit)) {
                    const dateText = (test.labTestFrequency || {}).unit === "STAT"
                        ? `Immediate`
                        : `${
                        test.labTestFrequency.count
                        } ${
                        test.labTestFrequency.unit.toLowerCase()
                        }${
                        test.labTestFrequency.count > 1 ? "s" : ""
                        }`;
                    labTestBody += getSpanTemplate("prescription-description").replace(
                        "$injectSpanData",
                        `&nbsp;- ${dateText}`
                    );
                }
                if (test.instructions && test.instructions !== AppConstants.LabtestConditions.RANDOM) {
                    labTestBody += getSpanTemplate("prescription-subdesc").replace(
                        "$injectSpanData",
                        `&nbsp;-&nbsp;${test.instructions}`
                    );
                }
                if (test.comments) {
                    labTestBody += getSpanTemplate("prescription-subdesc").replace(
                        "$injectSpanData",
                        `&nbsp;&#10222;&nbsp;${test.comments}&nbsp;&#10223;`
                    );
                }
                return labTestBodyTemplate.replace("$injectDivData", labTestBody);
            });
            return labTestContTemplate.replace("$injectLabTestBodyCont", labTestBodyItems.join("\n"));
        }
        return "";
    }

    const getMedicinesTemplate = () => {
        if (prescription && prescription.prescriptionMedicines && prescription.prescriptionMedicines.length > 0) {
            const medicineContTemplate: string = pdfTemplates.templateNameToDataMap["medicines"];
            const medicineBodyDataItems: Array<string> = prescription.prescriptionMedicines.map((medicine: TreatmentPlanMedicinesIndexObj) => {
                let medicineBodyData: string = "";
                let medicineRowTemplate = getDivTemplate("medicine-row");
                const medicineColTemplate = pdfTemplates.templateNameToDataMap["medicineColumn"]
                    .replace("$injectMedicineBrand", (medicine.brand && medicine.brand.displayValue || "").toUpperCase())
                    .replace(
                        "$injectMedicineNameAndDosage",
                        `${(medicine && medicine.genericName && medicine.genericName.displayValue || "").toUpperCase().replace(
                            "+",
                            ", "
                        )} ${medicine.dosage ? "| " + medicine.dosage : ""}`
                    );

                let medicineQuantityColTemplate = getDivTemplate("med-col-2").replace(
                    "$injectDivData",
                    !_isEmpty(medicine.medicineQuantity)
                        && !_isEmpty(medicine.medicineQuantity.count)
                        && !_isEmpty(medicine.medicineQuantity.unit)
                        ? `${(medicine.medicineQuantity.count || "")} ${AppUtils.internalToDisplayValue(medicine.medicineQuantity.unit || "")}`
                        : ""
                );

                let medicineDosageColTemplate = "";
                if (showDosageRow(medicine)) {
                    let rowData: string = "";
                    if (!_isEmpty(medicine.frequency)) {
                        rowData += getDivTemplate("dosage-col col-strt prescription-subheading").replace("$injectDivData", `${medicine.frequency}`);
                    }
                    if (!_isEmpty(medicine.frequency) && !_isEmpty(medicine.medicineDuration) && medicine.medicineDuration.count && medicine.medicineDuration.unit) {
                        rowData += getDivTemplate("col-center dosage-divider").replace("$injectDivData", "for");
                    }
                    if (!_isEmpty(medicine.medicineDuration) && medicine.medicineDuration.unit) {
                        let durationText = medicine.medicineDuration.unit === "SOS"
                            ? "As & when required"
                            : `${medicine.medicineDuration.count || ""} ${medicine.medicineDuration.unit.toLowerCase()}${medicine.medicineDuration.count > 1 ? "s" : ""}`
                        if (medicine.medicineDuration.unit === "SOS" && durationText.length > 0) {
                            rowData += getDivTemplate("col-center dosage-divider-2").replace("$injectDivData", "|");
                        }
                        rowData += getDivTemplate("dosage-col col-strt prescription-subheading").replace("$injectDivData", durationText)
                    }
                    medicineDosageColTemplate = getDivTemplate("med-dosage-col").replace("$injectDivData", getDivTemplate("dosage-row").replace("$injectDivData", rowData));
                }
                medicineRowTemplate = medicineRowTemplate.replace("$injectDivData", medicineColTemplate + medicineQuantityColTemplate + medicineDosageColTemplate);

                medicineBodyData += medicineRowTemplate;

                if (checkInstructionsExist(medicine)) {
                    let medicineInstructionContTemplate = pdfTemplates.templateNameToDataMap["medicineInstruction"];
                    let medicineInstructionBody = "";
                    if (!_isEmpty(medicine.medicineDosage)) {
                        const medicineSlotList = AppUtils.formatSlots(medicine.medicineDosage)
                        medicineInstructionBody += getLiTemplate("prescription-subdesc").replace("$injectLiData", medicineSlotList);
                    }
                    if (!_isEmpty(medicine.medicineInstructions)) {
                        medicineInstructionBody += getLiTemplate("prescription-subdesc").replace(
                            "$injectLiData",
                            AppConstants.MedicineInstructionCodes[medicine.medicineInstructions[0].internalValue]
                        );
                    }
                    if (medicine.comments) {
                        medicineInstructionBody += getLiTemplate("prescription-subdesc").replace("$injectLiData", medicine.comments);
                    }

                    medicineBodyData += medicineInstructionContTemplate.replace("$injectMedicineInstructionsData", medicineInstructionBody);
                }

                return pdfTemplates.templateNameToDataMap["medicineItem"]
                    .replace("$injectMedicineIndex", (medicine.arrIndex + 1).toString())
                    .replace("$injectMedicineItemBody", medicineBodyData);
            });
            return medicineContTemplate.replace("$injectMedicineBody", medicineBodyDataItems.join("\n"));
        }
        return "";
    }

    const getReferralTemplate = () => {
        if (prescription && prescription.referral && prescription.referral.length > 0 && !_isEmpty(prescription.referral)) {
            let referralContTemplate = pdfTemplates.templateNameToDataMap["referral"];
            const referralItems =
                prescription.referral.map((data: TreatmentPlanReferralObj) => {
                    let referralItemBody = "";
                    let specialist = null;
                    if (data && data.doctorSpecialityCode) {
                        specialist = AppUtils.getSpecialityNameFromCode(data.doctorSpecialityCode, props.staticSpecialistData);
                        referralItemBody += pdfTemplates.templateNameToDataMap["referralItemCode"].replace(
                            "$injectReferralCode",
                            specialist || data.doctorSpecialityCode
                        );
                    }
                    if (data.comment) {
                        referralItemBody += pdfTemplates.templateNameToDataMap["referralItemComment"].replace("$injectReferralComment", data.comment);
                    }
                    return getDivTemplate("referral-row-item").replace("$injectDivData", referralItemBody);
                }
                )
            return referralContTemplate.replace("$injectReferralBody", referralItems.join("\n"));
        }
        return "";
    }

    const getFollowupTemplate = () => {
        if (prescription && !_isEmpty(prescription.followup)) {
            let referralContTemplate = pdfTemplates.templateNameToDataMap["followUp"];
            const referralItems =
                prescription.followup.map((item: TreatmentPlanFollowUpObj) => {
                    let followupItemBody = "";
                    if (item.followUp && item.followUp.unit && item.followUp.count) {
                        followupItemBody += pdfTemplates.templateNameToDataMap["followUpItemDate"].replace(
                            "$injectFollowupDate",
                            `${item.followUp.count} ${AppUtils.toCapitalize(item.followUp.unit)}${item.followUp && item.followUp.count > 0 ? "s" : ""}`
                        );
                    }
                    if (item.comment) {
                        followupItemBody += pdfTemplates.templateNameToDataMap["followUpItemComment"].replace("$injectFollowupComment", item.comment);
                    }
                    if (!_isEmpty(followupItemBody)) {
                        return getDivTemplate("referral-row-item").replace("$injectDivData", followupItemBody);
                    }
                    else {
                        return "";
                    }
                })
            if (!_isEmpty(referralItems) && !_isEmpty(referralItems[0])) {
                return referralContTemplate.replace("$injectFollowupBody", referralItems.join("\n"));
            } else return "";
        }
        return "";
    }

    const getAdviceTemplate = () => {
        if (prescription && prescription.prescriptionAdvice && prescription.prescriptionAdvice.length > 0) {
            let adviceContTemplate = pdfTemplates.templateNameToDataMap["adviceTemplate"];
            const adviceItems = prescription.prescriptionAdvice.map((advice: TreatmentPlanAdviceObj) => {
                return pdfTemplates.templateNameToDataMap["adviceTemplateItem"]
                    .replace("$injectAdviceName", (advice.doctorAdvice && !_isEmpty(advice.doctorAdvice.displayValue))
                        ? advice.doctorAdvice.displayValue
                        : ""
                    )
                    .replace("$injectAdviceInstruction", "");
            });
            return adviceContTemplate.replace("$injectAdviceBody", adviceItems.join("\n"));
        }
        return "";
    }

    let currTemplate: string = getPrescriptionPdfTemplate();
    let injectedBody: string = "";

    const sectionTemplates = pdfTemplates.data.split(",").map((sectionId: string) => {
        const section = pdfTemplates.templateIdToNameMap[sectionId];
        switch (section) {
            case PrescriptionPdfSection.DIAGNOSIS:
                return getDiagnosisTemplate();
            case PrescriptionPdfSection.FOLLOWUP:
                return getFollowupTemplate();
            case PrescriptionPdfSection.LAB_TESTS:
                return getLabTestsTemplate();
            case PrescriptionPdfSection.MEDICINES:
                return getMedicinesTemplate();
            case PrescriptionPdfSection.GENERAL_ADVICE:
                return getAdviceTemplate();
            case PrescriptionPdfSection.REFERRAL:
                return getReferralTemplate();
            default:
                return ""
        }
    });
    injectedBody += sectionTemplates.join("\n");
    return currTemplate.replace("$injectPrescriptionBody", injectedBody);
}